import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaArrowRight, FaStar, FaHandshake, FaGraduationCap, FaGlobe, FaLightbulb, FaBriefcase } from 'react-icons/fa';

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Qui Sommes-Nous?</h1>
          <p className="text-xl sm:text-2xl font-semibold text-brand-accent mb-4">« L'excellence à proximité »</p>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto">
            Découvrez l'histoire, la vision, la mission et les valeurs de l'Université CEFAT International
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Tabs defaultValue="historique" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-12 bg-gray-100 p-2 rounded-lg">
            <TabsTrigger 
              value="historique" 
              data-testid="tab-historique"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-white font-semibold py-3 rounded-md transition-all text-sm sm:text-base"
            >
              Historique
            </TabsTrigger>
            <TabsTrigger 
              value="vision" 
              data-testid="tab-vision"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-white font-semibold py-3 rounded-md transition-all text-sm sm:text-base"
            >
              Vision
            </TabsTrigger>
            <TabsTrigger 
              value="mission" 
              data-testid="tab-mission"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-white font-semibold py-3 rounded-md transition-all text-sm sm:text-base"
            >
              Mission
            </TabsTrigger>
            <TabsTrigger 
              value="valeurs" 
              data-testid="tab-valeurs"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-white font-semibold py-3 rounded-md transition-all text-sm sm:text-base"
            >
              Valeurs
            </TabsTrigger>
          </TabsList>

          {/* Historique */}
          <TabsContent value="historique" className="animate-fade-in">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1679653226697-2b0fbf7c17f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4MTY3MTMzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Campus CEFAT"
                  className="rounded-lg shadow-2xl w-full"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-6 text-brand-primary">Historique et Évolution</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Fondée en <strong>mai 2006</strong> en Côte d'Ivoire, <strong>CEFAT INTERNATIONAL HOLDING</strong> est née de la vision de <strong>M. Aman Essé Honoré</strong>, avec pour ambition de contribuer durablement au développement du capital humain à travers la formation et le renforcement des compétences.
                  </p>
                  <p>
                    Aujourd'hui, le groupe poursuit son développement en s'appuyant sur l'innovation, la professionnalisation et une forte orientation vers l'employabilité.
                  </p>
                </div>
                
                <div className="mt-8 p-6 bg-brand-accent/30 rounded-lg border-l-4 border-brand-primary">
                  <h3 className="font-bold text-brand-secondary text-xl mb-4">Dates Clés</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <span className="bg-brand-primary text-white px-3 py-1 rounded font-bold text-sm">2006</span>
                      <span>Création de CEFAT INTERNATIONAL HOLDING</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-brand-primary text-white px-3 py-1 rounded font-bold text-sm">2019</span>
                      <span>Ouverture de l'Institut de Formation CEFAT International (IFCI)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-brand-primary text-white px-3 py-1 rounded font-bold text-sm">2022</span>
                      <span>Ouverture du Centre d'Immersion Linguistique</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-brand-primary text-white px-3 py-1 rounded font-bold text-sm">2024</span>
                      <span>Ouverture de l'Université CEFAT International</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="bg-brand-primary text-white px-3 py-1 rounded font-bold text-sm">2025</span>
                      <span>Ouverture de CEFAT Business School (CBS)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Vision */}
          <TabsContent value="vision" className="animate-fade-in">
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5 border-none shadow-lg">
              <h2 className="text-3xl font-bold mb-8 text-brand-primary text-center">Notre Vision</h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-xl text-center text-gray-700 leading-relaxed mb-8">
                  Devenir une <strong>université de référence en Côte d'Ivoire et en Afrique</strong>, reconnue pour la qualité de ses formations, son ouverture internationale et sa capacité à former des diplômés <strong>compétents, innovants et immédiatement opérationnels</strong>.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mt-12">
                  <Card className="p-6 bg-white shadow-md card-hover text-center">
                    <FaGraduationCap className="text-4xl text-brand-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3 text-brand-secondary">Qualité</h3>
                    <p className="text-gray-600">
                      Formations de qualité conformes aux standards internationaux
                    </p>
                  </Card>
                  <Card className="p-6 bg-white shadow-md card-hover text-center">
                    <FaGlobe className="text-4xl text-brand-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3 text-brand-secondary">Ouverture</h3>
                    <p className="text-gray-600">
                      Partenariats internationaux en France, Grande-Bretagne, Inde
                    </p>
                  </Card>
                  <Card className="p-6 bg-white shadow-md card-hover text-center">
                    <FaBriefcase className="text-4xl text-brand-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3 text-brand-secondary">Employabilité</h3>
                    <p className="text-gray-600">
                      Diplômés immédiatement opérationnels sur le marché
                    </p>
                  </Card>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Mission */}
          <TabsContent value="mission" className="animate-fade-in">
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-brand-secondary/5 to-brand-primary/5 border-none shadow-lg">
              <h2 className="text-3xl font-bold mb-8 text-brand-primary text-center">Notre Mission</h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
                  Offrir un enseignement supérieur de qualité, accessible et conforme aux standards internationaux, en mettant l'accent sur :
                </p>
                
                <div className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                      1
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-brand-secondary">La proximité avec les apprenants</h3>
                      <p className="text-gray-700">
                        Un accompagnement personnalisé à travers nos 5 campus (Abidjan, Abengourou, Bondoukou, Bonoua, Accra)
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                      2
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-brand-secondary">La professionnalisation et l'employabilité</h3>
                      <p className="text-gray-700">
                        Des formations pratiques orientées métiers et entrepreneuriat pour une insertion professionnelle réussie
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                      3
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-brand-secondary">L'innovation pédagogique</h3>
                      <p className="text-gray-700">
                        Des méthodes d'enseignement modernes, créatives et adaptées aux défis du monde actuel
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-primary text-white rounded-full flex items-center justify-center font-bold text-xl">
                      4
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-brand-secondary">L'ouverture internationale</h3>
                      <p className="text-gray-700">
                        Partenariats, mobilité étudiante et immersion linguistique pour une formation ouverte sur le monde
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-center mt-8 text-lg text-brand-secondary font-semibold">
                  Pour contribuer durablement au développement économique et social.
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* Valeurs */}
          <TabsContent value="valeurs" className="animate-fade-in">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-brand-primary">Nos Valeurs</h2>
              <p className="text-lg text-gray-600">Les fondements de notre engagement envers l'excellence</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <FaStar className="text-4xl text-brand-primary mb-4" />
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Excellence</h3>
                <p className="text-gray-700">
                  Qualité académique et rigueur au service de la réussite de chaque étudiant
                </p>
              </Card>
              
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <FaHandshake className="text-4xl text-brand-primary mb-4" />
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Proximité</h3>
                <p className="text-gray-700">
                  Accompagnement personnalisé et présence territoriale forte à travers nos 5 campus
                </p>
              </Card>
              
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <div className="text-4xl text-brand-primary mb-4">⚖️</div>
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Éthique</h3>
                <p className="text-gray-700">
                  Intégrité, responsabilité et respect des valeurs humaines dans toutes nos actions
                </p>
              </Card>
              
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <FaLightbulb className="text-4xl text-brand-primary mb-4" />
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Innovation</h3>
                <p className="text-gray-700">
                  Créativité, technologies et pédagogie moderne pour une formation d'avant-garde
                </p>
              </Card>
              
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <FaBriefcase className="text-4xl text-brand-primary mb-4" />
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Employabilité</h3>
                <p className="text-gray-700">
                  Formation pratique orientée métiers et entrepreneuriat pour l'insertion professionnelle
                </p>
              </Card>
              
              <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
                <FaGlobe className="text-4xl text-brand-primary mb-4" />
                <h3 className="text-xl font-bold mb-3 text-brand-secondary">Ouverture internationale</h3>
                <p className="text-gray-700">
                  Partenariats, mobilité et immersion linguistique pour une vision globale
                </p>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Pourquoi nous choisir */}
        <section className="mt-20 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 sm:p-12 text-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Pourquoi Nous Choisir ?</h2>
            <p className="text-lg text-gray-200">Les raisons qui font de CEFAT International votre meilleur choix</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="text-center p-6 bg-white/10 rounded-lg">
              <div className="text-4xl font-bold text-brand-accent mb-2">5</div>
              <p className="font-semibold">Campus en Afrique de l'Ouest</p>
            </div>
            <div className="text-center p-6 bg-white/10 rounded-lg">
              <div className="text-4xl font-bold text-brand-accent mb-2">+10</div>
              <p className="font-semibold">Filières d'excellence</p>
            </div>
            <div className="text-center p-6 bg-white/10 rounded-lg">
              <div className="text-4xl font-bold text-brand-accent mb-2">4</div>
              <p className="font-semibold">Pays partenaires</p>
            </div>
            <div className="text-center p-6 bg-white/10 rounded-lg">
              <div className="text-4xl font-bold text-brand-accent mb-2">19</div>
              <p className="font-semibold">Années d'expérience</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center flex-shrink-0">✓</div>
              <div>
                <h4 className="font-bold text-lg">Formation de qualité</h4>
                <p className="text-gray-200">Programmes conformes aux standards internationaux</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center flex-shrink-0">✓</div>
              <div>
                <h4 className="font-bold text-lg">Accompagnement personnalisé</h4>
                <p className="text-gray-200">Suivi individuel de chaque étudiant</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center flex-shrink-0">✓</div>
              <div>
                <h4 className="font-bold text-lg">Partenariats internationaux</h4>
                <p className="text-gray-200">France, Grande-Bretagne, Inde et plus</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-brand-accent rounded-full flex items-center justify-center flex-shrink-0">✓</div>
              <div>
                <h4 className="font-bold text-lg">Immersion linguistique</h4>
                <p className="text-gray-200">Centre dédié à Accra, Ghana</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a href="https://scolarite-cefat.net/preinscription">
              <Button 
                className="bg-white text-brand-primary hover:bg-brand-accent hover:text-brand-secondary px-8 py-6 text-lg font-semibold rounded-md shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1"
              >
                Se préinscrire maintenant
                <FaArrowRight className="ml-2 inline" />
              </Button>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
